import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Home, ArrowRight } from "lucide-react";
import { useAuth } from "../App";
import logoImage from "../assets/logo.svg";

const NotFound = () => {
  const { isAuthenticated, isLoading } = useAuth();

  const redirectPath = isAuthenticated ? "/mynotes" : "/";
  const buttonText = isAuthenticated ? "Go to My Notes" : "Go to Home";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex flex-col">
      {/* Logo */}
      <div className="p-6 flex justify-center">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link to="/">
            <img src={logoImage} alt="DocuMD" className="h-12" />
          </Link>
        </motion.div>
      </div>
      
      {/* Main Content */}
      <div className="flex-grow flex flex-col items-center justify-center p-4 md:p-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden relative z-10"
        >
          <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600" />
          
          <div className="px-8 pt-8 pb-6 text-center">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="mb-6"
            >
              <div className="w-32 h-32 mx-auto rounded-full bg-blue-50 flex items-center justify-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ 
                    delay: 0.5, 
                    duration: 0.5,
                    type: "spring",
                    stiffness: 300
                  }}
                >
                  <span className="text-8xl font-bold text-blue-500">404</span>
                </motion.div>
              </div>
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-3xl font-bold text-gray-900 mb-2"
            >
              Page Not Found
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="text-gray-600 mb-8"
            >
              The page you're looking for doesn't exist or has been moved.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="mb-4"
            >
              {isLoading ? (
                <div className="h-12 bg-gray-100 rounded-lg animate-pulse"></div>
              ) : (
                <Link 
                  to={redirectPath}
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/20 hover:shadow-blue-600/30 w-full"
                >
                  {buttonText}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              )}
            </motion.div>
          </div>
        </motion.div>
        
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full bg-blue-500/5"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                width: `${Math.random() * 300 + 50}px`,
                height: `${Math.random() * 300 + 50}px`,
              }}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ 
                opacity: [0, 0.5, 0], 
                scale: [0.8, 1.2, 1],
                x: [0, Math.random() * 100 - 50, 0],
                y: [0, Math.random() * 100 - 50, 0],
              }}
              transition={{
                duration: Math.random() * 10 + 15,
                repeat: Infinity,
                delay: i * 2,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default NotFound; 